import { CarePlusMedication } from "../../ChooseTreatmentCarePlus/types";
import { AVG_WEIGHT_LOSS, buildImageUrl, contentBrand } from "../helpers";
import { firstChoosableDosage } from "../../ChooseTreatmentCarePlus/useChooseTreatmentCarePlus";
import PriceValue from "./PriceValue";

type Translate = (key: string, fallback: string) => string;

const TwoTone = ({ value, caption }: { value: React.ReactNode; caption: React.ReactNode }) => (
  <p className="m-0 text-[12px] leading-[1.2]">
    <span className="font-bold text-[#08080a]">{value}</span>
    <br />
    <span className="font-normal text-[#99a3b2]">{caption}</span>
  </p>
);

const splitFirstWord = (text: string): [string, string] => {
  const [first, ...rest] = text.split(" ");
  return [first, rest.join(" ")];
};

const formatWeightLoss = (pct: string | undefined, language: string): string => {
  const value = parseFloat(pct ?? "");
  return Number.isFinite(value)
    ? new Intl.NumberFormat(language, { style: "percent", maximumFractionDigits: 1 }).format(value / 100)
    : "-";
};

type ComparisonField = {
  key: string;
  render: (med: CarePlusMedication, t: Translate, language: string) => React.ReactNode;
};

const COMPARISON_FIELDS: ComparisonField[] = [
  {
    key: "image",
    render: (med) => {
      const imageUrl = buildImageUrl(med.image);
      return (
        <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-[6px] bg-[#eee]">
          {imageUrl && <img src={imageUrl} alt={med.title} className="h-full w-full object-contain" />}
        </div>
      );
    },
  },
  {
    key: "title",
    render: (med) => (
      <p className="m-0 text-[16px] font-medium leading-[1.2] text-[#08080a] [overflow-wrap:anywhere] hyphens-auto">{med.title}</p>
    ),
  },
  {
    key: "weight_loss",
    render: (med, t, language) => (
      <TwoTone
        value={formatWeightLoss(AVG_WEIGHT_LOSS[contentBrand(med.title)]?.pct, language)}
        caption={t("comparison_weight_loss_short", "Ø weight loss*")}
      />
    ),
  },
  {
    key: "form",
    render: (med, t) => {
      const [value, caption] = splitFirstWord(
        contentBrand(med.title) === "wegovy_tablet"
          ? t("comparison_form_daily_intake", "Daily intake")
          : t("comparison_form_weekly_injection", "Weekly injection")
      );
      return <TwoTone value={value} caption={caption} />;
    },
  },
  {
    key: "advantage",
    render: (med, t) => {
      const advantageKey = AVG_WEIGHT_LOSS[contentBrand(med.title)]?.advantageKey;
      // hyphens-manual: the table-wide hyphens-auto split "sup-port" in this short copy.
      return <p className="m-0 text-[12px] font-medium leading-[1.2] text-[#08080a] hyphens-manual">{(advantageKey && t(advantageKey, "-")) || "-"}</p>;
    },
  },
  {
    key: "price",
    render: (med, t) => (
      <p className="m-0 leading-[1.2]">
        {/* Month 1 is the dose the page preselects (first in stock), not the cheapest one. */}
        <PriceValue value={firstChoosableDosage(med.dosages, med.is_pre_orderable)?.price ?? 0} className="text-[16px] font-bold text-[#08080a] max-[389px]:text-[14px]" />{" "}
        <span className="whitespace-nowrap text-[12px] font-normal text-[#99a3b2]">{t("comparison_first_month", "1st month")}</span>
      </p>
    ),
  },
];

// HL-12357: borders need border-solid (no Tailwind preflight). One grid filled column by column,
// so each row grows to its tallest cell and stays aligned across the medications.
const ComparisonTable = ({
  medications,
  t,
  language,
}: {
  medications: CarePlusMedication[];
  t: Translate;
  language: string;
}) => (
  <div
    lang={language}
    className="grid w-full overflow-hidden rounded-[16px] border border-solid border-[#dee3ed] bg-[#fafcfc] py-3 break-words hyphens-auto"
    style={{
      gridTemplateColumns: `repeat(${medications.length}, minmax(0, 1fr))`,
      gridTemplateRows: `91px repeat(${COMPARISON_FIELDS.length - 1}, auto)`,
      gridAutoFlow: "column",
    }}
  >
    {medications.map((med, column) =>
      COMPARISON_FIELDS.map((field) => (
        <div
          key={`${med.catalog_id}_${field.key}`}
          className={`flex min-w-0 flex-col justify-center px-3 ${field.key === "price" ? "" : "pb-5"} ${
            column > 0 ? "border-0 border-l border-solid border-[#dee3ed]" : ""
          }`}
        >
          {field.render(med, t, language)}
        </div>
      ))
    )}
  </div>
);

export default ComparisonTable;
